import { useEffect, useState } from 'react';
import axios from 'axios';
import { END_POINT, getJwtToken } from '../../Redux/AdminReducer/action';

const authConfig = () => {
  const token = getJwtToken();
  return token ? { headers: { Authorization: token } } : {};
};

const readError = (error, fallback) =>
  error?.response?.data?.message || error?.message || fallback;

const rangeQuery = ({ startDate, endDate, metrics } = {}) => {
  const params = new URLSearchParams();
  if (startDate) params.set('startDate', startDate);
  if (endDate) params.set('endDate', endDate);
  if (metrics) params.set('metrics', metrics);
  const query = params.toString();
  return query ? `?${query}` : '';
};

/**
 * Two series only share an axis if they cover the same months. With a range set
 * the server returns identical buckets, but with the range cleared each metric
 * reports only the months it has, so the sortable `keys` are unioned here
 * rather than trusting one series' labels for both.
 */
export const alignSeries = (metrics) => {
  const union = new Map();
  metrics.forEach((metric) => {
    (metric.keys || []).forEach((key, index) => {
      if (!union.has(key)) union.set(key, metric.labels?.[index] ?? key);
    });
  });

  if (!union.size) {
    const labels = metrics[0]?.labels || [];
    return { labels, series: metrics.map((metric) => metric.data || []) };
  }

  const keys = [...union.keys()].sort();
  return {
    labels: keys.map((key) => union.get(key)),
    series: metrics.map((metric) => {
      const byKey = new Map((metric.keys || []).map((key, index) => [key, metric.data?.[index] ?? 0]));
      return keys.map((key) => byKey.get(key) ?? 0);
    }),
  };
};

/**
 * One metric on its own range. Only used when a card overrides the page
 * filter - the rest of the board rides on the single batch call.
 */
export const fetchMetric = async (key, range) => {
  const { data } = await axios.get(
    `${END_POINT}/graphs/${encodeURIComponent(key)}${rangeQuery(range)}`,
    authConfig(),
  );
  return data;
};

/**
 * The catalogue is fetched once and gives the canonical order of the board;
 * the batch call re-runs whenever the range moves. Both are aggregated server
 * side, so no raw patient, shipment or therapy list is pulled into the browser.
 */
export default function useGraphMetrics({ startDate, endDate }, keys) {
  const [catalogue, setCatalogue] = useState([]);
  const [state, setState] = useState({ metrics: {}, failed: {}, loading: true, error: '', status: 0 });

  const wanted = Array.isArray(keys) && keys.length ? keys.join(',') : '';

  useEffect(() => {
    if (wanted) {
      setCatalogue([]);
      return undefined;
    }

    let cancelled = false;

    axios
      .get(`${END_POINT}/graphs`, authConfig())
      .then(({ data }) => {
        if (!cancelled) setCatalogue(Array.isArray(data?.metrics) ? data.metrics : []);
      })
      .catch(() => {
        if (!cancelled) setCatalogue([]);
      });

    return () => {
      cancelled = true;
    };
  }, [wanted]);

  useEffect(() => {
    let cancelled = false;
    setState((previous) => ({ ...previous, loading: true, error: '', status: 0 }));

    axios
      .get(
        `${END_POINT}/graphs/batch${rangeQuery({ startDate, endDate, metrics: wanted })}`,
        authConfig(),
      )
      .then(({ data }) => {
        if (cancelled) return;
        setState({
          metrics: data?.metrics || {},
          failed: data?.failed || {},
          loading: false,
          error: '',
          status: 200,
        });
      })
      .catch((error) => {
        if (cancelled) return;
        setState({
          metrics: {},
          failed: {},
          loading: false,
          error: readError(error, 'Could not load graphs'),
          status: error?.response?.status || 0,
        });
      });

    return () => {
      cancelled = true;
    };
  }, [startDate, endDate, wanted]);

  return { catalogue, ...state };
}
